// Formulaire public rempli par la ou les personnes à accréditer. Aucune authentification : la
// seule protection est le token, non devinable, présent dans l'URL. Sans aucun chrome applicatif
// (pas de Sidebar/nav — voir features/accreditation-external/PublicAccreditationApp.js).
//
// Deux présentations selon le formulaire créé par l'organisation :
//  - standard : une carte par personne (bandeau dégradé, jours en lignes cliquables) ;
//  - « chef de pôle » : tableau d'équipe pleine page, cf. PoleTeamForm.
// Habillage repris du design importé (claude.ai/design, « Formulaire Accreditation Public v2 »).
//
// Plusieurs personnes peuvent être ajoutées dans la même soumission (ex. un groupe qui
// s'inscrit ensemble) : dès qu'il y en a plus d'une, chacune doit avoir un email valide et
// distinct des autres, car c'est l'email qui identifie le compte cashless (cf. validation
// miroir côté serveur, routes/accreditationPublicForm.js).
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container, Paper, Box, Stack, Typography, TextField, Button, Alert, CircularProgress,
  Select, MenuItem, FormControl, InputLabel, IconButton, Divider,
} from '@mui/material';
import { Plus, Trash2, ShieldCheck, Lock, Check, Copy, Link2 } from 'lucide-react';
import { getAccreditationPublicForm, submitAccreditationPublicForm } from '../../api/api';
import { categoryVisual, BRAND_GRADIENT } from './formVisuals';
import PublicFormHeader from './PublicFormHeader';
import PoleTeamForm from './PoleTeamForm';
import { eventDatesLabel } from './eventDates';

const PageShell = ({ config, children }) => (
  <Box sx={{ minHeight: '100vh', bgcolor: '#EEF3F6' }}>
    <PublicFormHeader
      projectName={config?.projectName} datesLabel={eventDatesLabel(config?.eventDays)} maxWidth={640}
    />
    {children}
  </Box>
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyPerson = () => ({ nom: '', prenom: '', email: '', presentDays: [], customValues: {} });

const trackingUrl = (token) => `${window.location.origin}/accreditation-status/${token}`;

const formatDayDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const AccreditationPublicFormPage = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [config, setConfig] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [tracking, setTracking] = useState([]);
  const [sentCount, setSentCount] = useState(0);
  const [copiedToken, setCopiedToken] = useState('');

  const [people, setPeople] = useState([emptyPerson()]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getAccreditationPublicForm(token);
        if (!cancelled) setConfig(data);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.status === 404
            ? 'Ce lien de formulaire est invalide, expiré ou a été clôturé.'
            : "Erreur lors du chargement du formulaire.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Envoi commun aux deux présentations ; rejette en cas d'échec (chacune affiche son erreur).
  const sendPeople = async (payload) => {
    const result = await submitAccreditationPublicForm(token, { people: payload });
    setTracking(Array.isArray(result?.tracking) ? result.tracking : []);
    setSentCount(payload.length);
    setSubmitted(true);
  };

  const patchPerson = (idx, fields) => {
    setPeople((prev) => prev.map((p, i) => (i === idx ? { ...p, ...fields } : p)));
  };

  const toggleDay = (idx, label) => {
    setPeople((prev) => prev.map((p, i) => {
      if (i !== idx) return p;
      const has = p.presentDays.includes(label);
      return { ...p, presentDays: has ? p.presentDays.filter((d) => d !== label) : [...p.presentDays, label] };
    }));
  };

  const patchCustomValue = (idx, key, value) => {
    setPeople((prev) => prev.map((p, i) => (i === idx ? { ...p, customValues: { ...p.customValues, [key]: value } } : p)));
  };

  const addPerson = () => setPeople((prev) => [...prev, emptyPerson()]);
  const removePerson = (idx) => setPeople((prev) => prev.filter((_, i) => i !== idx));

  // Au-delà d'une personne, chaque email doit être renseigné et valide, et distinct des autres
  // (compte cashless individuel) — cf. bandeau d'explication en tête de doc.
  const emailIssues = useMemo(() => {
    if (people.length <= 1) return {};
    const seen = new Map();
    const issues = {};
    people.forEach((p, idx) => {
      const email = p.email.trim().toLowerCase();
      if (!email || !EMAIL_RE.test(email)) {
        issues[idx] = 'Email valide requis';
        return;
      }
      if (seen.has(email)) {
        issues[idx] = 'Email déjà utilisé plus haut';
        issues[seen.get(email)] = 'Email déjà utilisé plus bas';
      }
      seen.set(email, idx);
    });
    return issues;
  }, [people]);

  // Un champ personnalisé obligatoire doit avoir une valeur non vide pour CHAQUE personne —
  // validation client en plus de celle du serveur (source de vérité), pour un retour immédiat.
  const customFieldIssues = useMemo(() => {
    const fields = config?.customFields || [];
    if (fields.length === 0) return {};
    const issues = {};
    people.forEach((p, idx) => {
      const missing = fields.filter((f) => f.required && !String(p.customValues?.[f.key] || '').trim());
      if (missing.length > 0) issues[idx] = missing.map((f) => f.label).join(', ');
    });
    return issues;
  }, [people, config]);

  const canSubmit = people.every((p) => p.nom.trim())
    && Object.keys(emailIssues).length === 0
    && Object.keys(customFieldIssues).length === 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await sendPeople(people.map((p) => ({
        nom: p.nom.trim(),
        prenom: p.prenom.trim(),
        email: p.email.trim(),
        presentDays: config?.fullDuration ? [] : p.presentDays,
        customValues: p.customValues,
      })));
    } catch (err) {
      setSubmitError(err?.response?.status === 404
        ? 'Ce lien de formulaire est invalide, expiré ou a été clôturé.'
        : err?.response?.data?.error || "Erreur lors de l'envoi. Réessayez dans un instant.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageShell config={config}>
        <Container sx={{ py: 10, textAlign: 'center' }}>
          <CircularProgress />
        </Container>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell config={config}>
        <Container sx={{ py: 8, maxWidth: '480px !important' }}>
          <Alert severity="error">{error}</Alert>
        </Container>
      </PageShell>
    );
  }

  if (submitted) {
    const handleCopyTracking = async (t) => {
      const link = trackingUrl(t);
      try {
        await navigator.clipboard.writeText(link);
        setCopiedToken(t);
        setTimeout(() => setCopiedToken(''), 2000);
      } catch {
        window.prompt('Copiez ce lien :', link);
      }
    };

    const count = tracking.length || sentCount;

    return (
      <PageShell config={config}>
        <Container sx={{ py: 8, maxWidth: '480px !important' }}>
          <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
            <ShieldCheck size={40} color="#00be9e" style={{ marginBottom: 12 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Demande{count > 1 ? 's' : ''} envoyée{count > 1 ? 's' : ''}
            </Typography>
            <Typography color="text.secondary">
              Merci, {count > 1 ? 'vos demandes ont' : 'votre demande a'} bien été enregistrée{count > 1 ? 's' : ''}.
              Vous serez prévenu·e une fois {count > 1 ? 'validées' : 'validée'}.
            </Typography>

            {tracking.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography sx={{ fontWeight: 600, mb: 0.5, textAlign: 'left' }}>
                  Votre lien de suivi
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, textAlign: 'left' }}>
                  Il n'y a pas d'envoi par email — enregistrez ce lien maintenant pour vérifier
                  plus tard si {tracking.length > 1 ? 'vos demandes ont' : 'votre demande a'} été validée{tracking.length > 1 ? 's' : ''}.
                </Typography>
                <Stack spacing={1}>
                  {tracking.map((t) => (
                    <Stack
                      key={t.token} direction="row" spacing={1} alignItems="center"
                      sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
                    >
                      <Link2 size={14} color="#9da6ae" style={{ flexShrink: 0 }} />
                      <Typography sx={{ fontSize: 13, flex: 1, textAlign: 'left' }} noWrap>
                        {[t.prenom, t.nom].filter(Boolean).join(' ')}
                      </Typography>
                      <IconButton size="small" onClick={() => handleCopyTracking(t.token)}>
                        {copiedToken === t.token ? <Check size={15} color="#007739" /> : <Copy size={15} />}
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              </>
            )}
          </Paper>
        </Container>
      </PageShell>
    );
  }

  // Formulaire « chef de pôle » : tableau d'équipe pleine page.
  if (config?.mode === 'pole') {
    return <PoleTeamForm token={token} config={config} onConfigChange={setConfig} onSubmit={sendPeople} />;
  }

  const hasDays = !config?.fullDuration && (config?.eventDays || []).length > 0;
  const category = categoryVisual(config?.label);

  return (
    <PageShell config={config}>
      <Container sx={{ py: { xs: 3, md: 6 }, maxWidth: '640px !important' }}>
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ borderRadius: '20px', overflow: 'hidden', boxShadow: '0 8px 24px -8px rgb(0 47 54 / 0.10)' }}>

            <Box sx={{ px: { xs: 3, sm: 4 }, py: 3.5, background: BRAND_GRADIENT }}>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{
                display: 'inline-flex', height: 22, px: 1.25, borderRadius: 999,
                bgcolor: 'rgba(255,255,255,0.16)', mb: 1,
              }}>
                <category.icon size={11} color="#fff" />
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#fff' }}>
                  {config?.label || 'Accréditation'}
                </Typography>
              </Stack>
              <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', color: '#fff', fontSize: { xs: 22, sm: 26 } }}>
                {config?.projectName || "Demande d'accréditation"}
              </Typography>
              {config?.welcomeText && (
                <Typography sx={{ mt: 1, fontSize: 13, lineHeight: 1.6, color: 'rgba(255,255,255,0.85)', whiteSpace: 'pre-line' }}>
                  {config.welcomeText}
                </Typography>
              )}
            </Box>

            <Box component="form" onSubmit={handleSubmit} sx={{ px: { xs: 2.5, sm: 4 }, py: { xs: 3, sm: 4 } }}>
              <Stack spacing={3.5}>
                {people.map((p, idx) => (
                  <Box key={idx}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                        {idx === 0 ? 'Vos informations' : `Personne ${idx + 1}`}
                      </Typography>
                      <Box sx={{ flex: 1, height: '1px', bgcolor: '#e9edf1' }} />
                      {idx > 0 && (
                        <Button
                          size="small" color="error" startIcon={<Trash2 size={13} />}
                          onClick={() => removePerson(idx)}
                          sx={{ fontSize: 12, fontWeight: 600, minWidth: 0, px: 1 }}
                        >
                          Retirer
                        </Button>
                      )}
                    </Stack>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                      <TextField
                        label="Nom" value={p.nom} onChange={(e) => patchPerson(idx, { nom: e.target.value })}
                        required sx={{ gridColumn: { sm: '1 / -1' } }}
                      />
                      <TextField label="Prénom" value={p.prenom} onChange={(e) => patchPerson(idx, { prenom: e.target.value })} />
                      <TextField
                        label="Email" type="email" value={p.email} onChange={(e) => patchPerson(idx, { email: e.target.value })}
                        required={people.length > 1}
                        error={Boolean(emailIssues[idx])}
                        helperText={emailIssues[idx] || (people.length > 1 ? 'Requis, différent pour chaque personne' : '')}
                      />
                    </Box>

                    {hasDays && (
                      <Box sx={{ mt: 2 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                          Jours de présence
                        </Typography>
                        <Stack spacing={1}>
                          {config.eventDays.map((d) => {
                            const checked = p.presentDays.includes(d.label);
                            return (
                              <Box
                                key={d.label}
                                onClick={() => toggleDay(idx, d.label)}
                                sx={{
                                  display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1.25,
                                  border: '1px solid', borderColor: checked ? 'primary.main' : 'divider',
                                  bgcolor: checked ? 'rgba(0,190,158,0.06)' : '#fff',
                                  borderRadius: '10px', cursor: 'pointer', transition: 'all 150ms ease',
                                }}
                              >
                                <Box sx={{
                                  width: 18, height: 18, borderRadius: '5px', flexShrink: 0,
                                  border: '1.5px solid', borderColor: checked ? 'primary.main' : '#c7cfd5',
                                  bgcolor: checked ? 'primary.main' : '#fff',
                                  display: 'grid', placeItems: 'center',
                                }}>
                                  {checked && <Check size={11} color="#fff" strokeWidth={3} />}
                                </Box>
                                <Typography sx={{ fontSize: 14, fontWeight: 600, flex: 1 }}>{d.label}</Typography>
                                {formatDayDate(d.date) && (
                                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{formatDayDate(d.date)}</Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Stack>
                      </Box>
                    )}

                    {(config?.customFields || []).length > 0 && (
                      <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                        {config.customFields.map((field) => (
                          field.type === 'select' ? (
                            <FormControl key={field.key} size="small" required={field.required}>
                              <InputLabel>{field.label}</InputLabel>
                              <Select
                                label={field.label}
                                value={p.customValues?.[field.key] || ''}
                                onChange={(e) => patchCustomValue(idx, field.key, e.target.value)}
                              >
                                <MenuItem value=""><em>— Choisir —</em></MenuItem>
                                {field.options.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                              </Select>
                            </FormControl>
                          ) : (
                            <TextField
                              key={field.key}
                              size="small"
                              label={field.label}
                              required={field.required}
                              value={p.customValues?.[field.key] || ''}
                              onChange={(e) => patchCustomValue(idx, field.key, e.target.value)}
                            />
                          )
                        ))}
                      </Box>
                    )}
                    {customFieldIssues[idx] && (
                      <Typography sx={{ fontSize: 12, color: 'error.main', mt: 0.5 }}>
                        Champ requis manquant : {customFieldIssues[idx]}
                      </Typography>
                    )}
                  </Box>
                ))}

                <Button
                  startIcon={<Plus size={14} />} onClick={addPerson} variant="text"
                  sx={{ alignSelf: 'flex-start', fontWeight: 700, px: 0.5 }}
                >
                  Ajouter une personne
                </Button>

                {submitError && <Alert severity="error">{submitError}</Alert>}

                <Stack spacing={1.5}>
                  <Button
                    type="submit" size="large" fullWidth disabled={submitting || !canSubmit}
                    sx={{
                      height: 46, fontWeight: 700,
                      background: canSubmit ? BRAND_GRADIENT : undefined,
                      color: canSubmit ? '#fff' : undefined,
                      borderBottom: canSubmit ? '4px solid #00be9e' : undefined,
                      boxShadow: canSubmit ? '0 8px 20px -2px rgba(0,37,43,0.36)' : 'none',
                    }}
                  >
                    {submitting ? 'Envoi…' : people.length > 1 ? 'Envoyer les demandes' : 'Envoyer ma demande'}
                  </Button>
                  <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                    <Lock size={11} color="#9da6ae" />
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', textAlign: 'center' }}>
                      Vos informations sont transmises de façon sécurisée à l'organisateur et ne sont
                      utilisées que pour le traitement de votre accréditation.
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Box>
          </Paper>

          <Typography sx={{ fontSize: 12, color: 'text.secondary', textAlign: 'center' }}>
            Formulaire propulsé par Wilout
          </Typography>
        </Stack>
      </Container>
    </PageShell>
  );
};

export default AccreditationPublicFormPage;
